import * as React from "react";
import { useAuth } from "../../context/AuthContext";
import { useResponsive } from "../../hooks/useResponsive";
import { useBlobUrl } from "../../hooks/useBlobUrl";
import { REFINED, BRAND } from "../../components/risikoanalyseStep/tokens";
import { displayName, userKuerzel } from "./userDisplay";

/**
 * Kopf der Profil-Seite (GitHub-artig): Kürzel-Avatar + „Mein Profil — {Name}"
 * + Rolle-Chip. Props-frei, zieht direkt aus `useAuth()`.
 *
 * Avatar-Styling 1:1 wie der Nav-User-Circle (`NavigationLayout.tsx:766-790`),
 * damit Header-Avatar und (in SP2 folgender) Nav-Circle identisch aussehen.
 */

/** Rolle → deutsches Label (M2-Default). */
function roleLabel(role: string | undefined): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "gfgl":
      return "GF/GL";
    case "service":
      return "Service";
    case "mitarbeiter":
    default:
      return "Mitarbeiter";
  }
}

export const ProfileHeader: React.FC = () => {
  const { user, roles } = useAuth();
  const { isPhone } = useResponsive();

  const kuerzel = userKuerzel(user);
  const name = displayName(user);
  const chip = roleLabel(roles[0]);

  // Entra-Profilfoto (authentifiziert als Blob), Fallback = Kürzel wie bisher.
  const photoRef = user?.id ? `/users/${encodeURIComponent(user.id)}/photo` : null;
  const { url: photoUrl, loading: photoLoading, error: photoError } = useBlobUrl(photoRef);
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => {
    setImgError(false);
  }, [photoUrl]);
  const showPhoto = !!photoUrl && !imgError;
  // T-85: Ohne Foto still nur das Kürzel zu zeigen, wirkte wie ein Fehler.
  // Erst nach dem Laden — sonst blitzte der Hinweis bei jedem Seitenaufruf auf.
  const ohneFoto = !!photoRef && !photoLoading && (photoError || imgError || !photoUrl);

  return (
    <div
      style={{
        display: "flex",
        alignItems: isPhone ? "flex-start" : "center",
        flexDirection: isPhone ? "column" : "row",
        gap: isPhone ? 12 : 16,
        marginBottom: isPhone ? 16 : 24,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: BRAND.gold,
          color: REFINED.aubDeep,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: REFINED.fontUI,
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          flexShrink: 0,
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        {showPhoto ? (
          <img
            src={photoUrl ?? undefined}
            alt=""
            onError={() => setImgError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          kuerzel
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontFamily: REFINED.fontUI,
            fontSize: isPhone ? "1.3rem" : "1.5rem",
            fontWeight: 700,
            lineHeight: 1.2,
            color: REFINED.ink,
          }}
        >
          Mein Profil — {name}
        </h1>
        <span
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: REFINED.aub50,
            color: REFINED.aub,
            border: `1px solid ${REFINED.aub100}`,
            borderRadius: 999,
            padding: "3px 12px",
            fontFamily: REFINED.fontUI,
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
          }}
        >
          {chip}
        </span>
        {ohneFoto ? (
          <p
            data-testid="profil-foto-hinweis"
            style={{
              margin: 0,
              maxWidth: 560,
              fontFamily: REFINED.fontUI,
              fontSize: "0.8rem",
              lineHeight: 1.45,
              color: REFINED.ink2,
            }}
          >
            Dein Profilbild kommt aus Microsoft 365. Hinterlege es in Teams (Profilbild oben
            rechts → Bild ändern) oder bitte um ein Foto an deiner Mitarbeiter-Akte in KMVS. Es
            erscheint spätestens nach einer Stunde.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ProfileHeader;
